import React, { useEffect, useState, useRef, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { getChatConfigDetail, getChatMessages, getAgentMemory } from '../../../api';
import { getSocket, disconnectSocketByChatId } from '../../../api/ws-connection';
import { OutcomeWsEvents } from '../../../api/ws-events';

const ChatDetails = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const [config, setConfig] = useState(null);
    const [messages, setMessages] = useState([]);
    const [newMessage, setNewMessage] = useState('');
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [socketInstance, setSocketInstance] = useState(null);
    const messagesEndRef = useRef(null);
    const [agentMemory, setAgentMemory] = useState(null);
    const [showMemory, setShowMemory] = useState(false);
    const [memoryLoading, setMemoryLoading] = useState(false);

    const scrollToBottom = () => {
        messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
    };

    useEffect(() => {
        scrollToBottom();
    }, [messages]);

    const addNewMessage = useCallback((message) => {
        setMessages((prev) => {
            // Check if message already exists (to avoid duplicates with temp messages)
            const exists = prev.find(m => m.id === message.id || (m.tempId && m.tempId === message.tempId));
            if (exists) return prev;
            return [...prev, message];
        });
    }, []);

    useEffect(() => {
        const token = localStorage.getItem('token');
        if (!token) {
            navigate('/');
            return;
        }

        const fetchDetails = async () => {
            try {
                setLoading(true);
                setError('');
                const configData = await getChatConfigDetail(id);
                setConfig(configData);
                
                if (configData?.chat?.id) {
                    const messagesData = await getChatMessages(configData.chat.id);
                    setMessages(messagesData || []);
                    
                    // Setup Socket after obtaining chat.id
                    try {
                        const socket = await getSocket(configData.chat.id, addNewMessage);
                        setSocketInstance(socket);
                    } catch (wsErr) {
                        console.error('Error setting up socket:', wsErr);
                    }
                }
            } catch (err) {
                setError(err.response?.data?.message || 'Failed to fetch chat details.');
                console.error('Error fetching chat detail:', err);
            } finally {
                setLoading(false);
            }
        };

        fetchDetails();

        return () => {
            if (config?.chat?.id) {
                disconnectSocketByChatId(config.chat.id);
            }
        };
    }, [id, navigate, addNewMessage, config?.chat?.id]);

    const handleSendMessage = (e) => {
        e.preventDefault();
        if (!newMessage.trim()) return;
        
        const messageData = newMessage.trim();
        
        // Create a temporary message object for better UX
        const tempId = `temp-${Date.now()}`;
        const tempMessage = {
            id: tempId,
            tempId: tempId,
            author_id: 'User', // Replace with actual currentUserId if available
            message_value: messageData,
            message_type: 'text',
            status: 'active',
            created_at: new Date().toISOString(),
            updated_at: new Date().toISOString()
        };

        // Add message to the list immediately
        setMessages(prev => [...prev, tempMessage]);
        setNewMessage('');

        if (socketInstance) {
            // eslint-disable-next-line no-unused-vars
            const { id, ...messageWithoutId } = tempMessage;
            socketInstance.emit(OutcomeWsEvents.message, messageWithoutId);
        }
    };

    const handleGetContext = async () => {
        if (!config?.chat?.id) return;
        
        try {
            setMemoryLoading(true);
            if (showMemory) {
                setShowMemory(false);
                return;
            }
            const memory = await getAgentMemory(config.chat.id);
            setAgentMemory(memory);
            setShowMemory(true);
        } catch (err) {
            console.error('Error fetching agent memory:', err);
            alert('Failed to fetch agent context');
        } finally {
            setMemoryLoading(false);
        }
    };

    const token = localStorage.getItem('token');
    if (!token) {
        return null;
    }

    return (
        <div style={{
            minHeight: '100vh',
            background: '#ccdff5',
            padding: '20px',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center'
        }}>
            <div style={{
                width: '100%',
                maxWidth: '900px',
                height: 'calc(100vh - 40px)',
                background: '#F6F6F6',
                borderRadius: '24px',
                display: 'flex',
                flexDirection: 'column',
                boxShadow: '0 12px 40px rgba(0, 0, 0, 0.12)',
                overflow: 'hidden'
            }}>
                {/* Header */}
                <div style={{
                    padding: '20px 30px',
                    background: '#ffffff',
                    borderBottom: '1px solid #eee',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '20px'
                }}>
                    <button
                        onClick={() => navigate(-1)}
                        style={{
                            padding: '8px 16px',
                            borderRadius: '12px',
                            border: 'none',
                            background: '#f0f4f8',
                            color: '#3c5a5d',
                            cursor: 'pointer',
                            fontWeight: '600',
                            transition: 'all 0.2s'
                        }}
                        onMouseEnter={(e) => e.target.style.background = '#e2e8f0'}
                        onMouseLeave={(e) => e.target.style.background = '#f0f4f8'}
                    >
                        &larr; Back
                    </button>
                    {config?.mate_avatar?.avatar_link && (
                        <img 
                            src={config.mate_avatar.avatar_link} 
                            alt={config.mate_avatar.name}
                            style={{
                                width: '45px',
                                height: '45px',
                                borderRadius: '50%',
                                objectFit: 'cover'
                            }}
                        />
                    )}
                    <div style={{ flex: 1 }}>
                        <h2 style={{ margin: 0, color: '#3c5a5d', fontSize: '1.4rem' }}>
                            {config?.mate_avatar?.name || 'Chat'}
                        </h2>
                        <span style={{ fontSize: '0.85rem', color: '#888' }}>
                            {config?.chat?.is_active ? 'Active' : 'Inactive'} • {config?.mate_avatar?.mate_type || 'N/A'}
                        </span>
                    </div>
                    {socketInstance && (
                        <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                            <div style={{ width: '8px', height: '8px', borderRadius: '50%', background: '#10b981' }} />
                            <span style={{ fontSize: '0.75rem', color: '#10b981', fontWeight: '600' }}>Live</span>
                        </div>
                    )}
                </div>

                {/* Messages Area */}
                <div style={{
                    flex: 1,
                    overflowY: 'auto',
                    padding: '30px',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '16px',
                    background: '#f8fafc'
                }}>
                    {loading ? (
                        <div style={{ textAlign: 'center', padding: '40px', color: '#707F81' }}>
                            Loading messages...
                        </div>
                    ) : error ? (
                        <div style={{ 
                            padding: '16px', 
                            background: '#fee2e2', 
                            color: '#dc2626', 
                            borderRadius: '12px',
                            textAlign: 'center'
                        }}>
                            {error}
                        </div>
                    ) : messages.length === 0 ? (
                        <div style={{ textAlign: 'center', padding: '40px', color: '#707F81' }}>
                            No messages yet. Start the conversation!
                        </div>
                    ) : (
                        messages.map((msg, index) => {
                            const isAssistant = msg.author_id === 'System' || msg.author_id === 'Assistant' || msg.author_id === 'AI';
                            const isUser = !isAssistant;
                            
                            return (
                                <div
                                    key={msg.id || index}
                                    style={{
                                        alignSelf: isUser ? 'flex-end' : 'flex-start',
                                        maxWidth: '75%',
                                        display: 'flex',
                                        flexDirection: 'column',
                                        alignItems: isUser ? 'flex-end' : 'flex-start'
                                    }}
                                >
                                    <div style={{
                                        background: isUser ? '#3c5a5d' : '#ffffff',
                                        color: isUser ? '#ffffff' : '#333333',
                                        padding: '12px 18px',
                                        borderRadius: isUser ? '20px 20px 4px 20px' : '20px 20px 20px 4px',
                                        boxShadow: '0 2px 8px rgba(0,0,0,0.05)',
                                        fontSize: '1rem',
                                        lineHeight: '1.5',
                                        whiteSpace: 'pre-wrap'
                                    }}>
                                        {msg.message_value}
                                    </div>
                                    <span style={{ 
                                        fontSize: '0.75rem', 
                                        color: '#999', 
                                        marginTop: '4px',
                                        padding: '0 4px'
                                    }}>
                                        {msg.created_at ? new Date(msg.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : ''}
                                        {msg.tempId && ' ...'}
                                    </span>
                                </div>
                            );
                        })
                    )}
                    <div ref={messagesEndRef} />
                </div>

                {/* Agent Memory Display */}
                {showMemory && (
                    <div style={{
                        background: '#1e293b',
                        color: '#f8fafc',
                        padding: '20px',
                        maxHeight: '300px',
                        overflowY: 'auto',
                        borderTop: '2px solid #334155',
                        fontSize: '0.85rem',
                        fontFamily: 'monospace',
                        whiteSpace: 'pre-wrap',
                        boxShadow: 'inset 0 2px 4px rgba(0,0,0,0.2)'
                    }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
                            <span style={{ color: '#94a3b8', fontWeight: 'bold' }}>Agent Memory (Raw JSON)</span>
                            <button 
                                onClick={() => setShowMemory(false)}
                                style={{
                                    background: 'transparent',
                                    border: 'none',
                                    color: '#94a3b8',
                                    cursor: 'pointer',
                                    fontSize: '1rem'
                                }}
                            >
                                ✕
                            </button>
                        </div>
                        {JSON.stringify(agentMemory, null, 2)}
                    </div>
                )}

                {/* Input Area */}
                <form 
                    onSubmit={handleSendMessage}
                    style={{
                        padding: '24px 30px',
                        background: '#ffffff',
                        borderTop: '1px solid #eee',
                        display: 'flex',
                        gap: '12px'
                    }}
                >
                    <input
                        type="text"
                        value={newMessage}
                        onChange={(e) => setNewMessage(e.target.value)}
                        placeholder="Type your message..."
                        style={{
                            flex: 1,
                            padding: '14px 20px',
                            borderRadius: '14px',
                            border: '1px solid #e2e8f0',
                            outline: 'none',
                            fontSize: '1rem',
                            transition: 'border-color 0.2s'
                        }}
                        onFocus={(e) => e.target.style.borderColor = '#3c5a5d'}
                        onBlur={(e) => e.target.style.borderColor = '#e2e8f0'}
                    />
                    <button
                        type="submit"
                        disabled={!newMessage.trim() || !socketInstance}
                        style={{
                            padding: '0 24px',
                            borderRadius: '14px',
                            border: 'none',
                            background: (newMessage.trim() && socketInstance) ? '#3c5a5d' : '#cbd5e1',
                            color: 'white',
                            fontWeight: '600',
                            cursor: (newMessage.trim() && socketInstance) ? 'pointer' : 'default',
                            transition: 'all 0.2s'
                        }}
                    >
                        Send
                    </button>
                    <button
                        type="button"
                        onClick={handleGetContext}
                        disabled={memoryLoading || !config?.chat?.id}
                        style={{
                            padding: '0 20px',
                            borderRadius: '14px',
                            border: '1px solid #3c5a5d',
                            background: 'transparent',
                            color: '#3c5a5d',
                            fontWeight: '600',
                            cursor: (memoryLoading || !config?.chat?.id) ? 'default' : 'pointer',
                            transition: 'all 0.2s',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '8px'
                        }}
                        onMouseEnter={(e) => {
                            if (!memoryLoading && config?.chat?.id) {
                                e.target.style.background = '#3c5a5d';
                                e.target.style.color = 'white';
                            }
                        }}
                        onMouseLeave={(e) => {
                            if (!memoryLoading && config?.chat?.id) {
                                e.target.style.background = 'transparent';
                                e.target.style.color = '#3c5a5d';
                            }
                        }}
                    >
                        {memoryLoading ? 'Loading...' : 'Get Context'}
                    </button>
                </form>
            </div>
        </div>
    );
};

export default ChatDetails;
